// pages/SignageBrokerageUpsertOfficeProduct.tsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { messageHandler } from "utils/messageHandler";
import { AGENTOFFICEPRODUCTADDEDIT_MESSAGES } from "utils/messages";
import OfficeProductForm, {
  OfficeProductFormValues,
  SelectOption,
} from "components/OfficeProductForm"; // <- your UI component
import { useBrandCategorySelector } from "components/BrandCategorySelector";
import { getAuthCookie } from "../../utils/authCookies";

type Brand = { id: string; name: string; image: string; status: string };
type Category = { id: string; name: string; image: string; status: string };

type OfficeAddress = {
  _id?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

type Office = {
  _id: string;
  officeName: string;
  officeType: string;
  officeAddress: OfficeAddress[]; // API returns array in add-file, single in edit-file; we normalize to array
};

type ProductAPI = {
  name: string;
  sku: string;
  description?: string;
  brand?: string;
  category?: string;
  item_condition?: string;
  purchase_price?: number;
  retail_price?: number;
  package_dimensions?: {
    length?: number;
    height?: number;
    width?: number;
    weight?: number;
  };
  variants?: Array<{
    inventory?: Array<{ available?: number }>;
  }>;
  threshold_qty?: number;
  image?: { url?: string };
  status?: string;
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const CONDITION_OPTIONS: SelectOption[] = [
  { value: "new", label: "New" },
  { value: "used", label: "Used" },
  { value: "damaged", label: "Damaged" },
];

export default function SignageBrokerageAddEditOfficeProduct() {
  const { firmId, officeId, productId } = useParams<{
    firmId: string;
    officeId?: string;
    productId?: string;
  }>();
  console.log("firmId", firmId);  

  const isEdit = Boolean(productId && officeId);
  const navigate = useNavigate();
  const token = getAuthCookie("sessionToken") || "";

  // data sources
  const [offices, setOffices] = useState<Office[]>([]);
  const [office, setOffice] = useState<Office | null>(null);
  const [product, setProduct] = useState<ProductAPI | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // ✅ Compute the ID to use for brand-category selector
  // For office products: use firmId (brokerage ID from route) - following AddAgentOfficeProduct pattern
  // When office is selected, we pass the route id (brokerage ID)
  const entityIdForBrandCategory = useMemo(() => {
    // For office route, always use the route id (brokerage/firm ID)
    return firmId || "";
  }, [firmId]);
  
  // ✅ Use brand-category selector hook
  const {
    selectedBrand,
    setSelectedBrand,
    selectedCategories,
    setSelectedCategories,
    brandOptions: brandOptionsFromHook,
    filteredCategories: categoryOptionsFromHook,
    loadingBrands,
    loadingCategories,
  } = useBrandCategorySelector(entityIdForBrandCategory, token);

  // images (UI-only, multiple previews but we will send 1st file if present)
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>(
    []
  );
  const [uploadedFileMeta, setUploadedFileMeta] = useState<{ alt_text: string; url: string }[]>(
    []
  );

  // ✅ Brands and categories are now loaded via useBrandCategorySelector hook

  // ---------- add-flow: offices by firm ----------
  useEffect(() => {
    if (isEdit || !firmId) return;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/offices-search?created_by.id=${firmId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        setOffices(Array.isArray(json) ? json : []);
      } catch {
        setOffices([]);
      }
    })();
  }, [isEdit, firmId, token]);

  // ---------- edit-flow: office + product ----------
  useEffect(() => {
    if (!isEdit || !officeId) return;
    (async () => {
      try {
        const [o, p] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/offices/${officeId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(
            `${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);
        const officeJson = await o.json();
        const productJson = await p.json();
        setOffice(
          officeJson
            ? {
                _id: officeJson._id || officeId,
                officeName: officeJson.officeName,
                officeType: officeJson.officeType,
                officeAddress: officeJson.officeAddress
                  ? [officeJson.officeAddress].flat()
                  : [],
              }
            : null
        );
        const prod = productJson?.product || null;
        setProduct(prod);
        
        // Initialize uploaded images if product has an image
        if (prod?.image?.url) {
          setUploadedFileMeta([{
            alt_text: prod.name || 'Product Image',
            url: prod.image.url
          }]);
        }
      } catch {
        setOffice(null);
        setProduct(null);
      }
    })();
  }, [isEdit, officeId, productId, token]);

  // ---------- options ----------
  const officeOptions: SelectOption[] = useMemo(
    () =>
      (offices || []).map((o) => ({
        value: o._id,
        label: o.officeName,
      })),
    [offices]
  );

  // ✅ Options from hook (already filtered)
  const brandOptions: SelectOption[] = useMemo(
    () => brandOptionsFromHook,
    [brandOptionsFromHook]
  );

  const categoryOptions: SelectOption[] = useMemo(
    () => {
      // ✅ Double-check: only return categories if brand is selected
      return selectedBrand ? categoryOptionsFromHook : [];
    },
    [categoryOptionsFromHook, selectedBrand]
  );

  // ✅ Refs to prevent circular updates and track previous values
  const isSyncingFromFormik = useRef(false);
  const isSyncingFromHook = useRef(false);
  const prevProductBrandId = useRef<string>("");
  const prevProductCategoryId = useRef<string>("");
  const prevFormikBrand = useRef<string>("");
  const prevFormikCategory = useRef<string>("");
  const prevSelectedBrand = useRef<string>("");
  const prevSelectedCategory = useRef<string>("");

  // ✅ Sync product's brand/category to hook when product loads (Edit mode) - Only once
  useEffect(() => {
    const productBrandId = (product as any)?.brand_id || product?.brand || "";
    if (productBrandId && productBrandId !== prevProductBrandId.current && productBrandId !== selectedBrand) {
      prevProductBrandId.current = productBrandId;
      // console.log("🔄 [Product→Hook] Setting brand from product:", productBrandId);
      setSelectedBrand(productBrandId);
    }
  }, [product, selectedBrand]);

  useEffect(() => {
    const productCategoryId = (product as any)?.category_id || product?.category || "";
    if (productCategoryId && productCategoryId !== prevProductCategoryId.current) {
      const currentFirst = selectedCategories[0] || "";
      if (productCategoryId !== currentFirst) {
        prevProductCategoryId.current = productCategoryId;
        // console.log("🔄 [Product→Hook] Setting category from product:", productCategoryId);
        setSelectedCategories([productCategoryId]);
      }
    }
  }, [product, selectedCategories]);

  // ---------- initial values ----------
  const initialValues: OfficeProductFormValues = useMemo(
    () => ({
      // add-only
      officeId: "",
      // shared
      name: product?.name || "",
      sku: product?.sku || "",
      qty: product?.variants?.[0]?.inventory?.[0]?.available ?? "",
      threshold_qty: product?.threshold_qty ?? "",
      brand: selectedBrand || (product as any)?.brand_id || "",
      category: selectedCategories[0] || (product as any)?.category_id || "",
      item_condition: product?.item_condition || "",
      length: String(product?.package_dimensions?.length ?? ""),
      height: String(product?.package_dimensions?.height ?? ""),
      width: String(product?.package_dimensions?.width ?? ""),
      weight: String(product?.package_dimensions?.weight ?? ""),
      description: product?.description || "",
      statuss: product?.status || "",
      // UI-only images
      images: null,
    }),
    [product, selectedBrand, selectedCategories]
  );

  // ---------- helpers ----------
  const uploadFile = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("uploadDir", isEdit ? "products" : "brands"); // mirrors your two files
    const res = await fetch(`${process.env.REACT_APP_API_URL}/images/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const json = await res.json();
    if (!res.ok || json?.error) throw new Error(json?.error || "Upload failed");
    return json.path as string;
  };

  const getOfficeById = (id: string): Office | null => {
    if (isEdit) return office || null;
    return (offices || []).find((o) => o._id === id) || null;
  };

  // ---------- formik ----------
  const baseFormik = useFormik<OfficeProductFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      name: Yup.string().min(2).required("Name is required"),
      sku: Yup.string().notRequired(),
      statuss: Yup.string().required("Status is required"),
      qty: Yup.number()
        .typeError("Quantity must be a number")
        .required("Quantity is required")
        .min(1, "Quantity must be at least 1"),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitting(true);

        // image (first file if provided)
        let imageUrl: string | undefined;
        if (previewFiles[0]?.file) {
          imageUrl = await uploadFile(previewFiles[0].file);
        }

        // office resolve
        const selectedOffice =
          getOfficeById(isEdit ? (officeId as string) : values.officeId) || undefined;

        // ✅ Find brand and category names from hook values
        const selectedBrandOption = brandOptionsFromHook.find(b => b.value === selectedBrand);
        const selectedCategoryOption = categoryOptionsFromHook.find(c => c.value === selectedCategories[0]);

        // payload (matches your originals)
        const payload = {
          inventory_type: "office",
          office: selectedOffice && {
            id: selectedOffice._id,
            officeName: selectedOffice.officeName,
            officeType: selectedOffice.officeType,
            officeAddress: {
              addressLine1: selectedOffice.officeAddress?.[0]?.addressLine1 || "",
              city: selectedOffice.officeAddress?.[0]?.city || "",
              state: selectedOffice.officeAddress?.[0]?.state || "",
              country: selectedOffice.officeAddress?.[0]?.country || "",
              zip: selectedOffice.officeAddress?.[0]?.zip || "",
            },
          },
          product: {
            name: values.name,
            description: values.description,
            sku: values.sku,
            qty: Number(values.qty || 0),
            brand: selectedBrandOption?.label || "",
            brand_id: selectedBrand || "",
            category: selectedCategoryOption?.label || "",
            category_id: selectedCategories[0] || "",
            item_condition: values.item_condition,
            purchase_price: 1,
            retail_price: 1,
            package_dimensions: {
              length: Number(values.length || 0),
              height: Number(values.height || 0),
              width: Number(values.width || 0),
              weight: Number(values.weight || 0),
            },
            variants: [
              {
                variant_name: values.name,
                sku: values.sku,
                attributes: null,
                purchase_price: 1,
                retail_price: 1,
                package_dimensions: {
                  length: Number(values.length || 0),
                  height: Number(values.height || 0),
                  width: Number(values.width || 0),
                  weight: Number(values.weight || 0),
                  weight_type: "lb",
                },
                inventory: [
                  {
                    on_hand: Number(values.qty || 0),
                    available: Number(values.qty || 0),
                    reserved: 0,
                  },
                ],
              },
            ],
            threshold_qty: Number(values.threshold_qty || 0),
            image: imageUrl ? { url: imageUrl } : undefined,
            status: values.statuss,
            is_rotational: false,
            linked_mode: "none",
            locked_status: "unlocked",
            linked_installer: null,
          },
        };

        let res: Response;
        if (isEdit) {
          // PUT /office/{officeId}/inventory/{productId}
          res = await fetch(
            `${process.env.REACT_APP_API_URL}/office/${officeId}/inventory/${productId}`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(payload),
            }
          ); // :contentReference[oaicite:2]{index=2}
        } else {
          // POST /office/{selectedOfficeId}/inventory
          const selectedOfficeId = values.officeId;
          res = await fetch(
            `${process.env.REACT_APP_API_URL}/office/${selectedOfficeId}/inventory`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(payload),
            }
          ); // :contentReference[oaicite:3]{index=3}
        }

        const json = await res.json();
        if (!res.ok || json?.error) {
          messageHandler.product.error(isEdit ? "update" : "create", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES[isEdit ? "update" : "create"].error));
          throw new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES[isEdit ? "update" : "create"].error);
        }

        messageHandler.product.success(isEdit ? "update" : "create");
        navigate(`/signage/brokerageFirm/${firmId}`, {
          state: {
            successMessage: isEdit
              ? "Office Inventory Updated successfully!"
              : "Office Inventory Created successfully!",
            hash: "#officeInventory",
          },
        });
      } catch (e: any) {
        messageHandler.product.error(isEdit ? "update" : "create", e);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ---------- image handlers for the UI component ----------
  const handleAddFiles = (files: File[]) => {
    const next = files.map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setPreviewFiles((prev) => [...prev, ...next]);
  };
  const handleRemovePreview = (index: number) => {
    setPreviewFiles((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(index, 1);
      if (removed?.url) URL.revokeObjectURL(removed.url);
      return copy;
    });
  };
  const handleRemoveUploaded = (index: number) => {
    setUploadedFileMeta((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy;
    });
  };

  // ✅ Monitor formik brand changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }

    const brandValue = baseFormik.values.brand || "";
    if (brandValue && brandValue !== prevFormikBrand.current && brandValue !== selectedBrand) {
      prevFormikBrand.current = brandValue;
      // console.log("🔄 [Formik→Hook] Brand changed:", brandValue);
      isSyncingFromFormik.current = true;
      setSelectedBrand(brandValue);
      setSelectedCategories([]); // Clear categories when brand changes
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [baseFormik.values.brand, selectedBrand]);

  // ✅ Monitor formik category changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }

    const categoryValue = baseFormik.values.category || "";
    const currentFirst = selectedCategories[0] || "";
    if (categoryValue && categoryValue !== prevFormikCategory.current && categoryValue !== currentFirst) {
      prevFormikCategory.current = categoryValue;
      // console.log("🔄 [Formik→Hook] Category changed:", categoryValue);
      isSyncingFromFormik.current = true;
      setSelectedCategories([categoryValue]);
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [baseFormik.values.category, selectedCategories]);

  // ✅ Monitor hook brand changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) {
      return;
    }

    if (selectedBrand && selectedBrand !== prevSelectedBrand.current && baseFormik.values.brand !== selectedBrand) {
      prevSelectedBrand.current = selectedBrand;
      // console.log("🔄 [Hook→Formik] Brand changed:", selectedBrand);
      isSyncingFromHook.current = true;
      baseFormik.setFieldValue("brand", selectedBrand, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    }
  }, [selectedBrand, baseFormik.values.brand]);

  // ✅ Monitor hook category changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) {
      return;
    }

    const firstCategory = selectedCategories[0] || "";
    if (firstCategory && firstCategory !== prevSelectedCategory.current && baseFormik.values.category !== firstCategory) {
      prevSelectedCategory.current = firstCategory;
      // console.log("🔄 [Hook→Formik] Category changed:", firstCategory);
      isSyncingFromHook.current = true;
      baseFormik.setFieldValue("category", firstCategory, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    } else if (!firstCategory && baseFormik.values.category) {
      // Clear category if hook has no selection
      if (prevSelectedCategory.current !== "") {
        prevSelectedCategory.current = "";
        // console.log("🔄 [Hook→Formik] Clearing category");
        isSyncingFromHook.current = true;
        baseFormik.setFieldValue("category", "", false);
        setTimeout(() => { isSyncingFromHook.current = false; }, 0);
      }
    }
  }, [selectedCategories, baseFormik.values.category]);

  // ---------- derived props for UI ----------
  return (
    <OfficeProductForm
      isEdit={isEdit}
      formik={baseFormik as any}
      officeOptions={officeOptions}
      brandOptions={brandOptions}
      categoryOptions={categoryOptions}
      statusOptions={STATUS_OPTIONS}
      conditionOptions={CONDITION_OPTIONS}
      previewFiles={previewFiles}
      uploadedFileMeta={uploadedFileMeta}
      onAddFiles={handleAddFiles}
      onRemovePreview={handleRemovePreview}
      onRemoveUploaded={handleRemoveUploaded}
      title={isEdit ? "Edit Product" : "Add Product"}
      onBack={() => navigate(-1)}
      onSubmit={() => baseFormik.handleSubmit()}
      submitting={submitting}
      baseUrl={process.env.REACT_APP_BASE_URL}
      requireSku={false}
    />
  );
};
